import React from "react";

import {
    Assets_STATUS_LIST,
    General_STATUS_LIST,
    GDPR_REPORT_STATUS_LIST,
    TasksPriorities,
    TasksStatuses,
    formatTaskAndRiskId,
} from "../constants/Common";

const createSVGFromText = (content, settings = {}) => {
    /**
     * The user can overwrite all the settings by sending the desired items
     */
    const defaultSettings = {
        svg: {
            'viewbox': '0 0 30 30',
            'width': '30',
            'height': '30',
            'style': 'background-color: rgb(239, 242, 247);'
        },
        text: {
            'x': 15,
            'y': 15,
            'dominant-baseline': 'middle',
            'text-anchor': 'middle',
            'style': 'font-familly: Inter, sans-serif;fill: rgb(80, 93, 105);font-weight:bold;font-size: 14px;'
        }
    };

    const SVG_NS = "http://www.w3.org/2000/svg";

    let svg = document.createElementNS(SVG_NS, 'svg');
    let text = document.createElementNS(SVG_NS, "text");

    const gotTextSettings = settings?.text || {};

    for (const attrKey in defaultSettings.text) {
        text.setAttributeNS(null, attrKey, (gotTextSettings[attrKey] || defaultSettings.text[attrKey]));
    }

    const gotSVGSettings = settings?.svg || {};

    for (const attrKey in defaultSettings.svg) {
        svg.setAttributeNS(null, attrKey, (gotSVGSettings[attrKey] || defaultSettings.svg[attrKey]));
    }

    text.textContent = content;

    svg.appendChild(text);

    return svg;
}

const generateLogDescription = (t, action, section, hardwareType) => {
    let desc = "";
    switch (action) {
        case "create":
            desc = `${t("A new")} ${t(hardwareType?.toLowerCase())} ${t(
                "was added to the records."
            )}`;
            break;
        case "delete":
            desc = `Remove ${section?.toLowerCase()} devices from the system's records.`;
            break;
        case "handover":
            desc = `Document the transfer of ownership or responsibility for data or equipment.`;
            break;
        case "add_owner":
            desc = `Register new owners for specific devices or software.`;
            break;
        case "delete_owner":
            desc = `Remove ownership records from the system.`;
            break;
        case "edit":
            desc = `Modify and refresh existing ${section?.toLowerCase()} information.`;
            break;
        case "add_module":
            desc = `Module is added`;
            break;
        case "remove_module":
            desc = `Module is removed`;
            break;
        case "change_responsible_person":
            desc = `Responsible person has been changed`;
            break;
        case "change_company":
            desc = "Company has been changed";
            break;
        case "change_position":
            desc = "Position has been changed";
            break;
        case "change_email":
            desc = "Email has been changed";
            break;
        case "change_phone_number":
            desc = "Phone number has been changed";
            break;
        case "change_responsible_department":
            desc = "Responsible department has been changed";
            break;
        case "dpia":
            desc = `Revise and update Data Protection Impact Assessment (DPIA) records for ${section?.toLowerCase()}.`;
            break;
        case "create_processing_activity":
            desc = 'A new activity was added.';
            break;
        case "create_ai_processing_activity":
            desc = 'A new activity was created using AI.';
            break;
        case "change_processing_activity":
            desc = `Answers in processing activity changed `;
            break;
        case "upload_csv":
            desc = t("Upload processing activity data from Excel file");
            break;
        case "download":
            desc = t("Download");
            break;
        case "toggle_flag":
            desc = t("Change flag");
            break;
        case "change_status":
            desc = t("Change Status");
            break;
        case "change_priority":
            desc = t("Change Priority");
            break;
        case "change_analyst":
            desc = t("Change Analyst Assigned");
            break;
        case "remove_processing_activity":
            desc = `An activity was deleted.`;
            break;
        case "update_processing_activity_module_name":
            desc = `Update the activity title.`;
            break;
        case "update_responsible_person":
            desc = 'The responsible person was changed.';
            break;
        case "add_processing_activity_module_name":
            desc = `A new module name was added to the processing activity.`;
            break;
        default:
            desc = "";
            break;
    }

    return t(desc);
};

const generateLogAction = (t, action) => {
    let desc = "";
    switch (action) {
        case "create":
            desc = t("Created");
            break;
        case "delete":
            desc = t("Deleted");
            break;
        case "handover":
            desc = t("Handover");
            break;
        case "add_owner":
            desc = t("Added a new owner");
            break;
        case "delete_owner":
            desc = t("Deleted an owner");
            break;
        case "edit":
            desc = t("Edit detail");
            break;
        case "add_module":
            desc = t("Module is added");
            break;
        case "remove_module":
            desc = t("Module is removed");
            break;
        case "change_responsible_person":
            desc = t("Responsible changed");
            break;
        case "change_company":
            desc = t("Company changed");
            break;
        case "change_position":
            desc = t("Position changed");
            break;
        case "change_email":
            desc = t("Email changed");
            break;
        case "change_phone_number":
            desc = t("Phone number changed");
            break;
        case "change_responsible_department":
            desc = t("Responsible department changed");
            break;
        case "dpia":
            desc = t("DPIA");
            break;
        case "create_processing_activity":
            desc = t("Create processing activity");
            break;
        case "change_processing_activity":
            desc = t("Update processing activity details");
            break;
        case "upload_csv":
            desc = t("Upload processing activity data from Excel file");
            break;
        case "download":
            desc = t("Download");
            break;
        case "toggle_flag":
            desc = t("Change flag");
            break;
        case "change_status":
            desc = t("Change Status");
            break;
        case "change_priority":
            desc = t("Change Priority");
            break;
        case "change_analyst":
            desc = t("Change Analyst Assigned");
            break;
        case "remove_processing_activity":
            desc = t("Removed processing activity");
            break;
        case "update_processing_activity_module_name":
            desc = t("Updated processing activity module name");
            break;
        case "add_processing_activity_module_name":
            desc = t("Added processing activity module name");
            break;
        default:
            desc = "";
            break;
    }

    return desc;
};

const historyLogQuestionnaireSlugs = {
    processing_activity: "Processing Activity",
    ai_risk_assessment: "AI Risk Assessment",
    fria_assessment: "FRIA Assessment",
    conformity_assessment: "Conformity Assessment",
    risk_assessment: "Risk Assessment",
    data_protection_impact_assessment: "Data Protection Impact Assessment",
    description_of_processing: "Description of Processing",
    determining_the_processing: "Determining the Processing",
    other_asset_processing_activity: "Processing Activity",
    other_asset_risk_assessment: "Risk Assessment",
    other_asset_data_protection_impact_assessment: "Data Protection Impact Assessment",
    data_subject_request: "Data Subject Request",
    data_breach_incident: "Data Breach Incident",
    data_deletion_request: "Data Deletion Request",
};

const toHistoryLogTitleCase = (value) =>
    value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");

const normalizeHistoryLogQuestionnaireSlug = (value) =>
    value.trim().toLowerCase().replace(/\s+/g, "_");

const hasRawHistoryLogChangeValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return false;
    }

    if (Array.isArray(value)) {
        return true;
    }

    if (typeof value === "object") {
        return Object.keys(value).length > 0;
    }

    return true;
};

const formatHistoryLogChangeValue = (value, depth = 0) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    if (depth > 6) {
        return "";
    }

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    if (Array.isArray(value)) {
        if (!value.length) {
            return "";
        }

        return value
            .map((item) => formatHistoryLogChangeValue(item, depth + 1))
            .filter(Boolean)
            .join(", ");
    }

    if (typeof value === "object") {
        const textFields = [
            "title",
            "extended_title",
            "name",
            "label",
            "text",
            "date",
            "filename",
            "file_name",
        ];

        for (const field of textFields) {
            if (value[field]) {
                return String(value[field]);
            }
        }

        if (value.name && value.file_size !== undefined && value.file_size !== null && value.file_size !== "") {
            return `${value.name} (${value.file_size})`;
        }

        if (value.answer !== undefined) {
            return formatHistoryLogChangeValue(value.answer, depth + 1);
        }

        if (value.answers !== undefined) {
            return formatHistoryLogChangeValue(value.answers, depth + 1);
        }

        if (value.children?.length) {
            return formatHistoryLogChangeValue(value.children, depth + 1);
        }

        if (value.value !== undefined && value.value !== null && value.value !== "") {
            return String(value.value);
        }
    }

    return "";
};

const getHistoryLogAnswerFallbackLabel = (value, t) => {
    const item = Array.isArray(value) ? value[0] : value;

    if (!item || typeof item !== "object") {
        return t("Previous answer");
    }

    const typeLabels = {
        text: t("Text answer"),
        textarea: t("Text answer"),
        file_upload: t("File attachment"),
        radiobox: t("Selected option"),
        checkbox: t("Selected options"),
        date: t("Date answer"),
        select: t("Selected option"),
        dropdown: t("Selected option"),
        country_list: t("Country selection"),
        legal_entity: t("Legal entity selection"),
        category: t("Category selection"),
    };

    if (item.type && typeLabels[item.type]) {
        return typeLabels[item.type];
    }

    if (item.id) {
        return t("Previous answer");
    }

    return t("Previous answer");
};

const resolveHistoryLogChangeValue = (value, t) => {
    const formatted = formatHistoryLogChangeValue(value);
    if (formatted) {
        return formatted;
    }

    if (Array.isArray(value) && !value.length) {
        return t("Empty");
    }

    if (hasRawHistoryLogChangeValue(value)) {
        return getHistoryLogAnswerFallbackLabel(value, t);
    }

    return "";
};

const historyLogCaseIdLabels = {
    data_subject_request: "Request ID",
    data_breach_incident: "Incident ID",
};

const getHistoryLogCaseIdLabel = (metadata, t) => {
    const slug = metadata?.questionnaire_slug || metadata?.questionnaire;

    if (slug && historyLogCaseIdLabels[slug]) {
        return t(historyLogCaseIdLabels[slug]);
    }

    return t("Case ID");
};

const getHistoryLogQuestionnaireLabel = (metadata, t) => {
    if (!metadata) {
        return null;
    }

    const slug = metadata.questionnaire_slug || metadata.questionnaire;

    if (slug && historyLogQuestionnaireSlugs[slug]) {
        return t(historyLogQuestionnaireSlugs[slug]);
    }

    if (metadata.questionnaire_title) {
        const titleSlug = normalizeHistoryLogQuestionnaireSlug(metadata.questionnaire_title);

        if (historyLogQuestionnaireSlugs[titleSlug]) {
            return t(historyLogQuestionnaireSlugs[titleSlug]);
        }

        return t(toHistoryLogTitleCase(metadata.questionnaire_title));
    }

    if (slug) {
        const internalQuestionnaireSlugs = new Set([
            "software",
            "other_asset",
            "general",
            "hardware",
        ]);

        if (internalQuestionnaireSlugs.has(slug)) {
            return null;
        }

        return t(slug);
    }

    return null;
};

const getHistoryLogQuestionnaireChanges = (metadata, t) => {
    const changes = metadata?.changes;
    if (!changes) return [];

    const mapChange = (question, change) => {
        if (Array.isArray(change?.changes) && change.changes.length) {
            const rawFrom = change.changes[0];
            const rawTo = change.changes[1];

            return {
                question: change?.title ?? change?.question ?? question,
                from: resolveHistoryLogChangeValue(rawFrom, t),
                to: resolveHistoryLogChangeValue(rawTo, t),
                hasChange: hasRawHistoryLogChangeValue(rawFrom) || hasRawHistoryLogChangeValue(rawTo),
            };
        }

        const rawFrom = change?.from;
        const rawTo = change?.to ?? change?.changed_to;

        return {
            question: change?.title ?? change?.question ?? question,
            from: resolveHistoryLogChangeValue(rawFrom, t),
            to: resolveHistoryLogChangeValue(rawTo, t),
            hasChange: hasRawHistoryLogChangeValue(rawFrom) || hasRawHistoryLogChangeValue(rawTo),
        };
    };

    if (Array.isArray(changes)) {
        return changes
            .map((change, index) => mapChange(
                change?.question ?? change?.title ?? `${index + 1}`,
                change
            ))
            .filter(({ hasChange, from, to }) => hasChange && (from || to));
    }

    if (typeof changes === "object") {
        return Object.entries(changes)
            .map(([question, change]) => mapChange(question, change))
            .filter(({ hasChange, from, to }) => hasChange && (from || to));
    }

    return [];
};

const renderHistoryLogQuestionnaireChanges = (metadata, t) => {
    const changes = getHistoryLogQuestionnaireChanges(metadata, t);

    if (!changes.length) {
        return undefined;
    }

    return (
        <div className="history-log-questionnaire-changes">
            {changes.map(({ question, from, to }, index) => (
                <div key={`${question}-${index}`} className="history-log-questionnaire-change-item">
                    <p className="history-log-questionnaire-change-question mb-2">
                        {question}
                    </p>
                    {from && (
                        <p className="history-log-questionnaire-change-value mb-1">
                            <span className="detail-label">{t("From")}: </span>
                            <span>{from}</span>
                        </p>
                    )}
                    {to && (
                        <p className="history-log-questionnaire-change-value mb-0">
                            <span className="detail-label">{t("To")}: </span>
                            <span>{to}</span>
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
};

const formatHistoryLogStatus = (status, t, statusList) => {
    if (status === null || typeof status === "undefined") {
        return undefined;
    }

    const normalized = String(status).toUpperCase();
    const config = statusList[normalized] || statusList[status];

    if (config?.title) {
        return t(config.title);
    }

    return t(String(status));
};

const formatHistoryLogCaseStatus = (status, t) => {
    const formatted = formatHistoryLogStatus(status, t, General_STATUS_LIST);

    if (formatted !== t(String(status))) {
        return formatted;
    }

    const numericStatus = Number(status);

    if (!Number.isNaN(numericStatus)) {
        const reportStatus = GDPR_REPORT_STATUS_LIST.find((item) => item.id === numericStatus);

        if (reportStatus?.titleKey) {
            return t(reportStatus.titleKey);
        }
    }

    return formatted;
};

const formatHistoryLogPriority = (priority, t) => {
    if (priority === null || typeof priority === "undefined") {
        return undefined;
    }

    const normalized = String(priority).toLowerCase();
    const config = TasksPriorities[normalized];

    if (config?.title) {
        return t(config.title);
    }

    return t(normalized);
};

const formatHistoryLogActiveInactive = (value, t) => {
    if (typeof value === "boolean") {
        return t(value ? "Active" : "Inactive");
    }

    const normalized = String(value).toUpperCase();

    if (normalized === "ACTIVE" || normalized === "1" || normalized === "TRUE") {
        return t("Active");
    }

    if (normalized === "INACTIVE" || normalized === "0" || normalized === "FALSE") {
        return t("Inactive");
    }

    return t(String(value));
};

const formatHistoryLogApplicable = (value, t) => {
    if (typeof value === "boolean") {
        return t(value ? "Yes" : "No");
    }

    if (value === null || typeof value === "undefined") {
        return undefined;
    }

    return t(String(value));
};

const renderHistoryLogWasNow = (was, now, t, formatValue) => {
    const format = formatValue || ((value) => t(String(value)));

    if (typeof was !== "undefined" && typeof now !== "undefined") {
        return (
            <>
                {t("Before")}: {format(was)} | {t("After")}: {format(now)}
            </>
        );
    }

    if (typeof now !== "undefined") {
        return format(now);
    }

    if (typeof was !== "undefined") {
        return format(was);
    }

    return undefined;
};

const renderHistoryLogAssetStatusWasNow = ({ otherFields, t }) =>
    renderHistoryLogWasNow(
        otherFields?.previous_status,
        otherFields?.status,
        t,
        (value) => formatHistoryLogStatus(value, t, Assets_STATUS_LIST),
    );

const renderHistoryLogCaseStatusWasNow = ({ otherFields, t }) =>
    renderHistoryLogWasNow(
        otherFields?.previous_status,
        otherFields?.status,
        t,
        (value) => formatHistoryLogCaseStatus(value, t),
    );

const renderHistoryLogTaskStatusWasNow = ({ otherFields, t }) =>
    renderHistoryLogWasNow(
        otherFields?.previous_status,
        otherFields?.status,
        t,
        (value) => formatHistoryLogStatus(value, t, TasksStatuses),
    );

const renderHistoryLogPriorityWasNow = ({ otherFields, t }) =>
    renderHistoryLogWasNow(
        otherFields?.previous_priority,
        otherFields?.priority ?? otherFields?.status,
        t,
        (value) => formatHistoryLogPriority(value, t),
    );

const renderHistoryLogActiveInactiveWasNow = ({ otherFields, t }) =>
    renderHistoryLogWasNow(
        otherFields?.previous_status,
        otherFields?.status,
        t,
        (value) => formatHistoryLogActiveInactive(value, t),
    );

const renderHistoryLogApplicableWasNow = ({ otherFields, t }) =>
    renderHistoryLogWasNow(
        otherFields?.previous_applicable,
        otherFields?.applicable,
        t,
        (value) => formatHistoryLogApplicable(value, t),
    );

const getHistoryLogTaskMainId = (otherFields) => {
    const questionnaire = otherFields?.questionnaire;

    if (questionnaire === "software") {
        return otherFields?.software_secondary_id
            ?? otherFields?.secondary_software_id
            ?? otherFields?.asset_id
            ?? otherFields?.software_id;
    }

    if (questionnaire === "other_asset") {
        return otherFields?.other_asset_secondary_id
            ?? otherFields?.asset_id
            ?? otherFields?.other_asset_id;
    }

    return otherFields?.secondary_case_id;
};

const getHistoryLogRiskMainId = (otherFields) => {
    const questionnaire = otherFields?.questionnaire;

    if (questionnaire === "software") {
        return otherFields?.software_secondary_id
            ?? otherFields?.secondary_software_id
            ?? otherFields?.asset_id
            ?? otherFields?.software_id;
    }

    if (questionnaire === "other_asset") {
        return otherFields?.other_asset_secondary_id
            ?? otherFields?.asset_id
            ?? otherFields?.other_asset_id;
    }

    return otherFields?.secondary_case_id;
};

const renderHistoryLogTaskId = ({ value, otherFields }) => {
    const secondaryTaskId = value ?? otherFields?.secondary_task_id;

    if (secondaryTaskId === null || typeof secondaryTaskId === "undefined") {
        return undefined;
    }

    return formatTaskAndRiskId(
        "task",
        getHistoryLogTaskMainId(otherFields),
        otherFields?.secondary_risk_id ?? secondaryTaskId,
        otherFields?.is_mitigation,
        otherFields?.questionnaire,
        secondaryTaskId,
    );
};

const renderHistoryLogRiskId = ({ value, otherFields }) => {
    const secondaryRiskId = value ?? otherFields?.secondary_risk_id;

    if (secondaryRiskId === null || typeof secondaryRiskId === "undefined") {
        return undefined;
    }

    return formatTaskAndRiskId(
        "risk",
        getHistoryLogRiskMainId(otherFields),
        secondaryRiskId,
        otherFields?.is_mitigation,
        otherFields?.questionnaire,
        null,
    );
};

const convertHistoryLogHtmlToText = (value) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    const html = String(value);

    if (typeof DOMParser !== "undefined") {
        const doc = new DOMParser().parseFromString(html, "text/html");
        return doc.body.textContent || "";
    }

    return html.replace(/<[^>]*>/g, "");
};

const renderHistoryLogPlainText = ({ value }) => {
    if (value === null || value === undefined || value === "") {
        return undefined;
    }

    const text = convertHistoryLogHtmlToText(value).trim();
    return text || undefined;
};

const getHistoryLogAttachmentLabel = (item) => {
    if (item === null || item === undefined || item === "") {
        return "";
    }

    if (typeof item === "string" || typeof item === "number") {
        return String(item);
    }

    if (typeof item === "object") {
        return (
            item.origin_name ||
            item.original_name ||
            item.name ||
            item.filename ||
            item.file_name ||
            item.title ||
            ""
        );
    }

    return "";
};

const renderHistoryLogAttachments = ({ value }) => {
    if (value === null || value === undefined || value === "") {
        return undefined;
    }

    if (Array.isArray(value)) {
        const labels = value
            .map(getHistoryLogAttachmentLabel)
            .filter(Boolean);

        return labels.length ? labels.join(", ") : undefined;
    }

    const label = getHistoryLogAttachmentLabel(value);
    return label || undefined;
};

const formatHistoryLogFileSize = (bytes, decimals = 1) => {
    const size = Number(bytes);

    if (!Number.isFinite(size) || size < 0) {
        return undefined;
    }

    if (size === 0) {
        return "0 Bytes";
    }

    const k = 1000;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(size) / Math.log(k));

    return `${parseFloat((size / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const renderHistoryLogFileSize = ({ value }) => {
    if (value === null || value === undefined || value === "") {
        return undefined;
    }

    return formatHistoryLogFileSize(value);
};

const getFirstDefinedValue = (...values) =>
    values.find((value) => value !== undefined && value !== null);

const mergeHistoryLogDetailsMetadata = (logData) => {
    if (!logData) {
        return logData;
    }

    const { metadata = {}, ...rest } = logData;

    return {
        ...rest,
        metadata: {
            ...metadata,
            secondary_tom_id: getFirstDefinedValue(
                metadata.secondary_tom_id,
                metadata.secondaryTomId,
                rest.secondary_tom_id,
                rest.secondaryTomId,
            ),
            source_secondary_tom_id: getFirstDefinedValue(
                metadata.source_secondary_tom_id,
                metadata.sourceSecondaryTomId,
                rest.source_secondary_tom_id,
                rest.sourceSecondaryTomId,
            ),
            software_secondary_id: getFirstDefinedValue(
                metadata.software_secondary_id,
                metadata.secondary_software_id,
                rest.software_secondary_id,
                rest.secondary_software_id,
            ),
            hardware_secondary_id: getFirstDefinedValue(
                metadata.hardware_secondary_id,
                metadata.hardware_id,
                rest.hardware_secondary_id,
                rest.hardware_id,
            ),
            other_asset_secondary_id: getFirstDefinedValue(
                metadata.other_asset_secondary_id,
                metadata.other_asset_id,
                rest.other_asset_secondary_id,
                rest.other_asset_id,
            ),
            secondary_case_id: getFirstDefinedValue(
                metadata.secondary_case_id,
                rest.secondary_case_id,
            ),
            legal_entity_secondary_id: getFirstDefinedValue(
                metadata.legal_entity_secondary_id,
                rest.legal_entity_secondary_id,
            ),
            files: getFirstDefinedValue(
                metadata.files,
                rest.files,
            ),
            attachments: getFirstDefinedValue(
                metadata.attachments,
                rest.attachments,
            ),
            file_name: getFirstDefinedValue(
                metadata.file_name,
                metadata.origin_name,
                metadata.original_name,
                metadata.filename,
                metadata.name,
                rest.file_name,
                rest.origin_name,
                rest.original_name,
                rest.filename,
                rest.name,
            ),
            module_name: metadata.module_name || rest.module_name,
        },
    };
};

export {
    createSVGFromText,
    generateLogDescription,
    generateLogAction,
    getHistoryLogQuestionnaireLabel,
    getHistoryLogCaseIdLabel,
    getHistoryLogQuestionnaireChanges,
    renderHistoryLogQuestionnaireChanges,
    renderHistoryLogAssetStatusWasNow,
    renderHistoryLogCaseStatusWasNow,
    renderHistoryLogTaskStatusWasNow,
    renderHistoryLogPriorityWasNow,
    renderHistoryLogActiveInactiveWasNow,
    renderHistoryLogApplicableWasNow,
    renderHistoryLogTaskId,
    renderHistoryLogRiskId,
    renderHistoryLogPlainText,
    renderHistoryLogAttachments,
    renderHistoryLogFileSize,
    mergeHistoryLogDetailsMetadata,
};